<template>
    <div class="flex flex-col gap-2">
        <div class="flex flex-col">
            <h2 class="text-xl font-extrabold m-0">Assign Organizational Units</h2>
            <span class="text-sm">You can check the <a :href="permissionChartLink" target="_blank" rel="noopener noreferrer" class="underline!">permission</a> chart to see what each role has access to within a OU.</span>
        </div>

        <div class="flex flex-col gap-2 md:flex-row">
            <div class="md:w-80">
                <vx-label>Organizational Unit</vx-label>
                <select-org-unit v-model="orgUnitModel" relative-dropdown size="lg" placeholder="" :disabled="props.disabled || limitReached"></select-org-unit>
            </div>
            <div class="flex grow gap-2">
                <div class="grow">
                    <vx-label>Role</vx-label>
                    <vx-selectable
                        v-model="roleModel"
                        :clearable="false"
                        relative-dropdown
                        :options="orgUnitRolesOptions()"
                        :reduce="option => option.value"
                        size="lg"
                        data-test="org-unit-role-select"
                        :disabled="props.disabled || limitReached"
                    ></vx-selectable>
                </div>
                <div class="pt-7">
                    <vx-button
                        @click="addOrgUnitAssignment"
                        :disabled="!orgUnitModel || !roleModel || props.disabled || limitReached"
                        data-test="org-unit-add"
                        size="lg"
                        type="button"
                    >
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>

        <div v-if="confirmedOrgUnits.length > 0">
            <vx-list-animated
                v-model="confirmedOrgUnits"
                label="name"
                value="id"
            >
                <template v-slot:item="{ item }">
                    <div class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2">
                        <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.name }}</div>
                        <div class="line-clamp-3 break-all">{{ orgUnitRolesOptions().find(r => r.value === item.role).label }}</div>
                    </div>
                </template>
            </vx-list-animated>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { VxButton, VxLabel, VxSelectable, VxListAnimated } from '@voxie/frontend-components';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { orgUnitRolesOptions, DEFAULT_ORG_UNIT_ROLE } from '~/constants/roles';
import { useToasted } from '~/composables/useToasted';
import { permissionChartLink } from '~/constants/groups';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';
import { restrictions } from '~/constants/orgUnits';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
});

const assignedOrgUnits = defineModel({
    type: Array,
    default: () => []
});

const toasted = useToasted();

const list = ref([{ id: '', role: DEFAULT_ORG_UNIT_ROLE, name: '' }]);

const orgUnitModel = computed({
    get: () => list.value[0]?.id ? { id: list.value[0].id, name: list.value[0].name } : null,
    set: (val) => {
        list.value[0].id = val?.id || '';
        list.value[0].name = val?.name || '';
    }
});

const roleModel = computed({
    get: () => list.value[0]?.role || DEFAULT_ORG_UNIT_ROLE,
    set: (val) => { list.value[0].role = val; }
});

const confirmedOrgUnits = computed({
    get: () => list.value.slice(1),
    set: (newValue) => {
        // When list is modified (e.g., items deleted), update the internal list
        list.value = [list.value[0], ...newValue];
    }
});

const limitReached = computed(() => list.value.length - 1 === restrictions.MAX_OU_PER_INVITE);

watch(list, (newList) => {
    const validEntries = newList.filter((item, index) => {
        if (index === 0) {
            return item.id && item.role;
        }
        return true;
    });
    assignedOrgUnits.value = validEntries;
}, { deep: true, immediate: true });

const addOrgUnitAssignment = () => {
    const draft = list.value[0];
    if (!draft.id || !draft.role) {
        return;
    }

    const existingOrgUnit = list.value.slice(1).find((orgUnit) => orgUnit.id === draft.id);
    if (existingOrgUnit) {
        toasted.error('Organizational Unit is already assigned');
        return;
    }

    list.value.push({ ...draft });

    list.value[0] = { id: '', role: DEFAULT_ORG_UNIT_ROLE, name: '' };
};

const reset = () => {
    list.value = [{ id: '', role: DEFAULT_ORG_UNIT_ROLE, name: '' }];
};

defineExpose({
    reset,
});
</script>
